/**
 * InsufficientCreditsModal — "Not enough credits": the quoted download costs
 * more than the workspace balance (or the free usage is spent). Admins can
 * jump to Plans & Billing to buy credits; Editors and Viewers are told to ask
 * an Admin. This is the only modal a refused download opens — callers suppress
 * the global FreeUsageExhausted modal for gated requests.
 */
import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, SparkModalPortal, Text } from '@spark';

import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { BILLING_TAB, settingsTabPath } from '@/features/settings/components/settingsTabs.js';

import { formatMoney } from '../utils/billingFormat';
import '../billing.css';

export const INSUFFICIENT_CREDITS_MODAL_WIDTH = 520;
export const INSUFFICIENT_CREDITS_TITLE = 'Not enough credits';
export const INSUFFICIENT_CREDITS_CTA_ADMIN = 'Buy Credits';
export const INSUFFICIENT_CREDITS_CTA_MEMBER = 'Close';
export const INSUFFICIENT_CREDITS_ASK_ADMIN = 'Contact a workspace admin to add credits.';

const CURRENCY = 'usd';

export const creditsNeededCents = (totalCents, balanceCents) =>
    Math.max((Number(totalCents) || 0) - (Number(balanceCents) || 0), 0);

export default function InsufficientCreditsModal({ open = false, totalCents = 0, balanceCents = 0, onClose }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUsecaseId } = useContext(GlobalContext) || {};
    const { isAuthEnabled, role } = useAuth() || {};
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const needed = creditsNeededCents(totalCents, balanceCents);

    const close = () => onClose?.();
    const buyCredits = () => {
        close();
        const from = `${location.pathname}${location.search}${location.hash}`;
        navigate(settingsTabPath(BILLING_TAB.id, currentUsecaseId), { state: { from } });
    };

    return (
        <SparkModalPortal open={open} onClose={close} width={INSUFFICIENT_CREDITS_MODAL_WIDTH} destroyOnClose>
            <Modal
                open
                className="billing-dialog insufficient-credits-modal"
                feedbackType="warning"
                title={INSUFFICIENT_CREDITS_TITLE}
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                showTertiaryButton={isAdmin}
                primaryLabel={isAdmin ? INSUFFICIENT_CREDITS_CTA_ADMIN : INSUFFICIENT_CREDITS_CTA_MEMBER}
                tertiaryLabel="Cancel"
                onPrimaryClick={isAdmin ? buyCredits : close}
                onTertiaryClick={close}
                onCloseClick={close}
            >
                <div className="billing-dialog__body" data-testid="insufficient-credits-modal">
                    <Text as="p" variant="base-regular" color="secondary" className="billing-dialog__copy">
                        {`This download costs ${formatMoney(totalCents, CURRENCY)}. Your workspace balance is ${formatMoney(balanceCents, CURRENCY)}.`}
                    </Text>
                    {needed > 0 ? (
                        <Text as="p" variant="base-semibold" color="primary" className="billing-dialog__copy">
                            {`Credits needed: ${formatMoney(needed, CURRENCY)}`}
                        </Text>
                    ) : null}
                    {isAdmin ? null : (
                        <Text as="p" variant="base-regular" color="secondary" className="billing-dialog__copy">
                            {INSUFFICIENT_CREDITS_ASK_ADMIN}
                        </Text>
                    )}
                </div>
            </Modal>
        </SparkModalPortal>
    );
}
